import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Music, ExternalLink, Copy, Download, Share2, Filter, Loader2, ArrowLeft } from 'lucide-react';
import { getEventById, getEventByUrl, Event } from '../../services/events';
import { getPlayedRequests, getUnplayedRequests, SongRequest } from '../../services/requests';
import toast from 'react-hot-toast';

type PlaylistFilter = 'played' | 'unplayed' | 'both';

export default function PlaylistPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [playedSongs, setPlayedSongs] = useState<SongRequest[]>([]);
  const [unplayedSongs, setUnplayedSongs] = useState<SongRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<PlaylistFilter>('played');

  useEffect(() => {
    const load = async () => {
      if (!eventId) return;

      // Try by ID first, then by URL slug
      let ev = await getEventById(eventId);
      if (!ev) ev = await getEventByUrl(eventId);
      if (!ev) {
        setLoading(false);
        return;
      }
      setEvent(ev);

      const [played, unplayed] = await Promise.all([
        getPlayedRequests(ev.id),
        getUnplayedRequests(ev.id),
      ]);
      // Filter out local files excluded from playlist
      setPlayedSongs(played.filter(r => r.includeInPlaylist !== false));
      setUnplayedSongs(unplayed.filter(r => r.includeInPlaylist !== false));
      setLoading(false);
    };
    load();
  }, [eventId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Music className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Event Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400">This playlist doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (event.status !== 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Music className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Playlist Not Available Yet</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The playlist for "{event.name}" will be available after the event ends.
          </p>
          <Link to={`/e/${event.eventUrl}`} className="btn btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Event
          </Link>
        </div>
      </div>
    );
  }

  const displaySongs = filter === 'played' ? playedSongs
    : filter === 'unplayed' ? unplayedSongs
    : [...playedSongs, ...unplayedSongs];

  const formatPlaylist = (songs: SongRequest[]): string => {
    return songs.map((s, i) => `${i + 1}. ${s.song.title} - ${s.song.artist}`).join('\n');
  };

  const handleCopy = () => {
    const text = formatPlaylist(displaySongs);
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Playlist copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleDownload = () => {
    const text = formatPlaylist(displaySongs);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.name} - Playlist.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Playlist downloaded!');
  };

  const handleShare = async () => {
    const text = formatPlaylist(displaySongs);
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.name} Playlist`,
          text,
        });
      } catch {
        // User cancelled share
      }
    } else {
      handleCopy();
    }
  };

  const hasSpotifyPlayed = !!event.spotifyPlaylistUrl;
  const hasSpotifyUnplayed = !!event.spotifyUnplayedPlaylistUrl;
  const missingSongsPlayed = playedSongs.filter(s => !s.spotifyUri).length;
  const missingSongsUnplayed = unplayedSongs.filter(s => !s.spotifyUri).length;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to={`/e/${event.eventUrl}`}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{event.name}</h1>
          <p className="text-gray-600 dark:text-gray-400">Event Playlist</p>
        </div>
      </div>

      {/* Spotify Playlists */}
      {(hasSpotifyPlayed || hasSpotifyUnplayed) && (
        <div className="card p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Music className="w-5 h-5 text-green-500" />
            Spotify Playlists
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {hasSpotifyPlayed && (
              <a
                href={event.spotifyPlaylistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn bg-green-500 text-white hover:bg-green-600 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Played Songs
              </a>
            )}
            {hasSpotifyUnplayed && (
              <a
                href={event.spotifyUnplayedPlaylistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn bg-green-500/80 text-white hover:bg-green-600 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Didn't Make It
              </a>
            )}
          </div>
          {((hasSpotifyPlayed && missingSongsPlayed > 0) || (hasSpotifyUnplayed && missingSongsUnplayed > 0)) && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Note: Some songs may be missing from Spotify playlists if they weren't matched to Spotify tracks.
            </p>
          )}
        </div>
      )}

      {/* Filter & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as PlaylistFilter)}
            className="input text-sm py-1"
          >
            <option value="played">Played Songs ({playedSongs.length})</option>
            <option value="unplayed">Didn't Make It ({unplayedSongs.length})</option>
            <option value="both">All Songs ({playedSongs.length + unplayedSongs.length})</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleCopy} className="btn btn-secondary btn-sm flex items-center gap-1.5">
            <Copy className="w-4 h-4" />
            Copy
          </button>
          <button onClick={handleDownload} className="btn btn-secondary btn-sm flex items-center gap-1.5">
            <Download className="w-4 h-4" />
            Download
          </button>
          <button onClick={handleShare} className="btn btn-secondary btn-sm flex items-center gap-1.5">
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>

      {/* Song List */}
      <div className="space-y-2">
        {displaySongs.length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500 dark:text-gray-400">No songs in this category.</p>
          </div>
        ) : (
          displaySongs.map((song, index) => (
            <div
              key={song.id}
              className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <span className="text-sm font-bold text-gray-400 w-8 text-center">{index + 1}</span>
              {song.song.albumArtUrl ? (
                <img src={song.song.albumArtUrl} alt={song.song.title} className="w-10 h-10 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                  <Music className="w-5 h-5 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{song.song.title}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{song.song.artist}</p>
              </div>
              {song.status === 'played' && (
                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full flex-shrink-0">
                  Played
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
